import './menu.scss';
import '../input/buttons-v1-1.scss';

import React, { PropsWithChildren, ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { observer } from 'mobx-react-lite';
import classNames from 'classnames';
import { MenuVDotsSVGIcon, MoreSVGIcon } from '../../components/svg';
import { usePopper } from 'react-popper';
import { Placement } from '@popperjs/core';

import { useOutsideClick, useDelayedUnmount } from '../../components/hooks';
import { ObjectLiteral } from '../../core';
import { Tooltip } from '../overlays';

export type MenuPopupProps = {

}

export const MenuPopup = observer((_props: MenuPopupProps) => {

  return (<div className="menu-popup"></div>);
});

export type MenuButtonProps = PropsWithChildren<{
  layout?: 'horizontal' | 'vertical',
  className?: string | ObjectLiteral,
  svgIcon?: ReactNode,
  placement?: Placement,
  portalToBody?: boolean,
  tooltip?: ReactNode,
  'aria-label'?: string,
}>;

export const MenuButton = observer((props: MenuButtonProps) => {

  const layout = props.layout || 'horizontal';
  const className = classNames(
    'btn btn-core btn-icon medium',
    props.className,
    props.layout || 'horizontal', {

  });

  const iconSvgElem = props.svgIcon ? (
    props.svgIcon
  ) : layout === 'vertical' ? (
    <MenuVDotsSVGIcon />
  ) : (
    <MoreSVGIcon />
  );

  const [isOpened, setIsOpened] = useState(false);
  const [forceClose, setForceClose] = useState(false);
  const { shouldRender, isAnimating } = useDelayedUnmount(isOpened && !forceClose, forceClose ? 0 : 400);

  // Listen for custom event to close this menu when another opens
  useEffect(() => {
    const handleCloseAll = () => {
      if (isOpened) {
        setForceClose(true);
        setIsOpened(false);
      }
    };
    document.addEventListener('dropdown:close-all', handleCloseAll);
    return () => document.removeEventListener('dropdown:close-all', handleCloseAll);
  }, [isOpened]);

  // Reset forceClose when menu is fully closed
  useEffect(() => {
    if (!isOpened && forceClose) {
      setForceClose(false);
    }
  }, [isOpened, forceClose]);

  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const [anchorElement, setAnchorElement] = useState<HTMLButtonElement | null>(null);
  const [arrowElement] = useState(null);

  const { styles, attributes } = usePopper(anchorElement, popperElement, {
    placement: props.placement || 'bottom-end',
    strategy: 'fixed',
    modifiers: [
      {
        name: 'arrow',
        options: {
          element: arrowElement
        }
      },

      {
        name: 'offset',
        options: {
          offset: [0, 5],
        }
      },

      {
        name: 'flip',
        options: {
          fallbackPlacements: ['left', 'top', 'bottom'],
        },
      }
    ],
  });

  useOutsideClick(popperElement, () =>
    setIsOpened(false));

  const dropdown = shouldRender ? (
    <div
      className={classNames('dropdown-menu menu-popup popup menu-animate', isAnimating)}
      ref={setPopperElement}
      style={styles.popper}
      {...attributes.popper}>
      <div className="popup-arrow" style={styles.arrow} />
      <div className="menu-items" onClick={_evt => setIsOpened(false)}>
        {props.children}
      </div>
    </div>
  ) : null;

  const shouldPortal = props.portalToBody && typeof document !== 'undefined';

  const button = (
    <button className={className}
        ref={setAnchorElement}
        aria-label={props['aria-label']}
        onClick={evt => {
          // Close other dropdowns/menus before opening this one
          if (!isOpened) {
            document.dispatchEvent(new CustomEvent('dropdown:close-all'));
          }
          setIsOpened(!isOpened);
          evt.stopPropagation();
        }}>

        <span className="icon">
          {iconSvgElem}
        </span>
      </button>
  );

  const trigger = props.tooltip ? (
    <Tooltip content={props.tooltip}>
      {button}
    </Tooltip>
  ) : button;

  return (
    <>
      {trigger}

      {shouldPortal ? createPortal(dropdown, document.body) : dropdown}
    </>
  )
});
